/**
 * usePartnerDashboard — Data hook for the Practitioner Affiliate Dashboard screen.
 *
 * Fetches real affiliate dashboard data via the partner-dashboard
 * edge function, with graceful fallback to default empty state.
 *
 * Interface contract matches the spec in SPEC.md §5 (Hook section).
 *
 * Task: NocoDB #2090 — Wire mock hook to real API
 * Epic: #1529 — DD Affiliate + Customer Referral Program
 */

import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { buildReferralUrl } from '../lib/url';
import { copyReferralUrl, shareReferralUrl } from '../lib/referral-sharing';
import { getEdgeFunctionUrl } from '../lib/api-url';
import { fetchJsonValidated } from '@dramadetector/api-client';
import {
  PartnerDashboardResultDto,
  type PartnerDashboardResult,
} from '@dramadetector/shared/schemas';
// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export interface PartnerEarnings {
  thisMonth: number;
  total: number;
  pending: number;
  commissionRate: number;
}

export interface PartnerFunnelStep {
  label: string;
  count: number;
  percentage: number;
}

export interface PartnerCertification {
  type: 'founding' | 'standard';
  date: string;
}

export type PartnerType = 'practitioner' | 'coach' | 'influencer' | 'corporate' | 'other';

export interface UsePartnerDashboardReturn {
  earnings: PartnerEarnings;
  referralLink: string;
  funnel: PartnerFunnelStep[];
  certification: PartnerCertification;
  partnerType: PartnerType;
  isLoading: boolean;
  error: Error | null;
  copyReferralLink: () => Promise<void>;
  shareReferralLink: (method: 'email' | 'sms' | 'qr') => Promise<void>;
}

// ---------------------------------------------------------------------------
// Default/empty state (shown while loading or on error)
// ---------------------------------------------------------------------------

const DEFAULT_EARNINGS: PartnerEarnings = {
  thisMonth: 0,
  total: 0,
  pending: 0,
  commissionRate: 0.20,
};

const DEFAULT_FUNNEL: PartnerFunnelStep[] = [
  { label: 'Invited', count: 0, percentage: 0 },
  { label: 'Signed Up', count: 0, percentage: 0 },
  { label: 'Subscribed', count: 0, percentage: 0 },
  { label: 'Active', count: 0, percentage: 0 },
];

const DEFAULT_CERTIFICATION: PartnerCertification = {
  type: 'standard',
  date: new Date().toISOString().slice(0, 7),
};

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const partnerDashboardKeys = {
  all: ['partner-dashboard'] as const,
  dashboard: (userId: string | null) =>
    [...partnerDashboardKeys.all, 'dashboard', userId] as const,
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function usePartnerDashboard(): UsePartnerDashboardReturn {
  const { user, accessToken } = useAuth();
  const userId = user?.id ?? null;

  // Fetch affiliate dashboard data from edge function
  const query = useQuery({
    queryKey: partnerDashboardKeys.dashboard(userId),
    queryFn: (): Promise<PartnerDashboardResult> =>
      fetchJsonValidated(
        PartnerDashboardResultDto,
        getEdgeFunctionUrl('partner-dashboard'),
      ),
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 15,
    enabled: !!accessToken && !!userId,
  });

  // earnings.thisMonth/total/pending come from Affonso (not yet integrated);
  // commissionRate is available from the DTO.
  const earnings: PartnerEarnings = useMemo(() => ({
    ...DEFAULT_EARNINGS,
    commissionRate: query.data?.commissionRate ?? DEFAULT_EARNINGS.commissionRate,
  }), [query.data?.commissionRate]);
  const referralLink = query.data?.referralCode ?? '';
  const funnel = query.data?.funnel ?? DEFAULT_FUNNEL;
  const certification = query.data?.certification ?? DEFAULT_CERTIFICATION;
  const partnerType = query.data?.partnerType ?? 'practitioner';

  const fullUrl = useMemo(
    () => referralLink ? buildReferralUrl(referralLink) : '',
    [referralLink]
  );

  const copyReferralLink = useCallback(
    () => copyReferralUrl(fullUrl),
    [fullUrl]
  );

  const shareReferralLink = useCallback(async (method: 'email' | 'sms' | 'qr') => {
    if (method === 'qr') {
      // QR bottom sheet is deferred — separate ticket.
      return;
    }
    await shareReferralUrl(fullUrl);
  }, [fullUrl]);

  return {
    earnings,
    referralLink: fullUrl,
    funnel,
    certification,
    partnerType,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error : null,
    copyReferralLink,
    shareReferralLink,
  };
}
